import { z, ZodError, ZodSchema } from "zod";
import { Option } from "../utils/types";

interface FormField {
    name: string;
    label: string;
    type: string;
    required?: boolean;
    options?: Option[];
    roleBased?: string[];
}

export const usPhoneNumberPattern = /^(?:\+?1[-.\s]?)?(?:\(\d{3}\)[-.\s]?|\d{3}[-.\s]?)\d{3}[-.\s]?\d{4}$/;
export const validateLogin = (data: { email: string; password: string }) => {
    const loginSchema = z.object({
        email: z.string().min(1, "Username is required").refine(val => {
            const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
            const isPhone = usPhoneNumberPattern.test(val);
            return isEmail || isPhone;
        }, { message: "Invalid username format" }),
        password: z.string().min(6, "Password must be at least 6 characters long"),
    });

    try {
        loginSchema.parse(data);
        return { success: true, data };
    } catch (error) {
        if (error instanceof z.ZodError) {
            return {
                success: false,
                errors: error.errors.map(err => ({ path: err.path[0], message: err.message }))
            };
        }
        return { success: false, errors: [{ path: 'unknown', message: 'An unexpected error occurred' }] };
    }
};

export const validateSignup = (data: { email: string; agreedTOS: boolean; agreedDriverConsent?: boolean; agreedClinicBAA?: boolean; role?: string }) => {
    const signupSchema = z.object({
        email: z.string().min(1, "Username is required"),
        agreedTOS: z.literal(true, {
            errorMap: () => ({ message: "You must agree to the Terms of Service" }),
        }),
        agreedDriverConsent: z.boolean().optional(),
        agreedClinicBAA: z.boolean().optional(),
        role: z.string().optional(),
    }).refine((val) => {
        if (val.role === "driver") {
            return val.agreedDriverConsent === true;
        }
        if (val.role === "clinic") {
            return val.agreedClinicBAA === true;
        }
        return true;
    }, {
        message: "Required consent not provided",
        path: ["agreedDriverConsent"],
    }).superRefine((val, ctx) => {
        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.email);
        const isPhone = usPhoneNumberPattern.test(val.email);
        
        if (val.role !== "driver" && !isEmail) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Valid email address is required",
                path: ["email"]
            });
        } else if (val.role === "driver" && !isEmail && !isPhone) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Invalid email or phone format",
                path: ["email"]
            });
        }
    });

    try {
        signupSchema.parse(data);
        return { success: true, data };
    } catch (error) {
        if (error instanceof z.ZodError) {
            return {
                success: false,
                errors: error.errors.map(err => ({ path: err.path[0], message: err.message })),
            };
        }
        return { success: false, errors: [{ path: 'unknown', message: 'An unexpected error occurred' }] };
    }
};

export const validatePassword = (data: { password: string; confirmPassword: string }) => {
    const passwordSchema = z.object({
        password: z.string()
            .min(8, "Password must be at least 8 characters long")
            .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
            .regex(/[a-z]/, "Password must contain at least one lowercase letter")
            .regex(/[0-9]/, "Password must contain at least one number")
            .regex(/[\W_]/, "Password must contain at least one special character (e.g., !, @, #, $)"),
        confirmPassword: z.string().min(1, "Confirmation is required"),
    }).refine((data) => data.password === data.confirmPassword, {
        message: "Passwords do not match",
        path: ["confirmPassword"],
    });

    try {
        passwordSchema.parse(data);
        return { success: true, data };
    } catch (error) {
        if (error instanceof z.ZodError) {
            return {
                success: false,
                errors: error.errors.map(err => ({ path: err.path[0], message: err.message })),
            };
        }
        return { success: false, errors: [{ path: 'unknown', message: 'An unexpected error occurred' }] };
    }
};

export const clinicOfficeSchema = z.object({
    address: z.string().min(1, 'Address is required'),
    phone_number: z.string().regex(
        /^(?:\+1\s?)?(?:\(?\d{3}\)?[\s.-]?)\d{3}[\s.-]?\d{4}$/,
        'Enter a valid US phone number'
    ),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    zip_code: z.string()
        .length(5, 'Zip code must be exactly 5 digits')
        .regex(/^\d{5}$/, 'Zip code must contain only digits'),
});

export const clinicUserSchema = z.object({
    full_name: z.string().min(1, "Full name is required"),
    type: z.string().min(1, "Type is required")
    ,
    nrcme_number: z
        .string().regex(/^\d+$/, "NRCME number must contain only digits")
        .nullable()
        .optional()

        .or(z.literal(null)),
    practitioner_license_number: z
        .string()
        .regex(/^\d+$/, "License number must contain only digits")
        .nullable()
        .optional()
        .or(z.literal(null)),
    status: z
        .enum(["Enabled", "Disabled"])
        .refine(
            (value) => ["Enabled", "Disabled"].includes(value),
            "Status must be either 'Enabled' or 'Disabled'"
        ),
});
// This function creates a Zod schema based on formFields
export const generateSchema = (fields: FormField[]): ZodSchema => {
    const shape: Record<string, any> = {};

    fields.forEach((field) => {
        let validator;

        // Define validation type based on field type
        switch (field.type) {
            case "text":
                validator = field.required
                    ? z.string().min(1, `${field.label} is required`)
                    : z.string().optional();
                break;
            case "number":
                validator = field.required
                    ? z.number().nonnegative(`${field.label} is required`)
                    : z.number().optional();
                break;
            case "phoneNumber":
                validator = field.required
                    ? z
                        .string()
                        .regex(usPhoneNumberPattern, `${field.label} must be a valid US phone number`)
                        .transform((val) => val.replace(/\D/g, "")) // Transform to only digits
                    : z
                        .string()
                        .regex(usPhoneNumberPattern, `${field.label} must be a valid US phone number`)
                        .transform((val) => (val ? val.replace(/\D/g, "") : undefined)) // Optional transformation
                        .optional();
                break;
            case "date":
                validator = field.required
                    ? z.string().refine((val) => !isNaN(Date.parse(val)), {
                        message: "Invalid date format",
                    })
                    : z.string().optional();
                break;
            case "checkbox":
                validator = field.required ? z.boolean() : z.boolean().optional();
                break;
            case "select":
                validator = field.required
                    ? z.string().min(1, `${field.label} is required`)
                    : z.string().optional();
                break;
            default:
                validator = field.required
                    ? z.string().min(1, `${field.label} is required`)
                    : z.string().optional();
                break;
        }

        shape[field.name] = validator;
    });

    return z.object(shape);
};

export const validateWithSchema = <T>(schema: ZodSchema<T>, data: unknown): { success: boolean; data?: T; errors?: { path: string | number; message: string }[] } => {
    try {
        const parsedData = schema.parse(data);
        return { success: true, data: parsedData };
    } catch (error) {
        if (error instanceof ZodError) {
            return {
                success: false,
                errors: error.errors.map(err => ({ path: err.path[0], message: err.message })),
            };
        }
        return { success: false, errors: [{ path: 'unknown', message: 'An unexpected error occurred' }] };
    }
};